import React, { PureComponent } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ButtonGroup,
  Container,
  Row
} from 'reactstrap';
import { Route, Switch } from 'react-router';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import { omit, filter } from 'underscore';
import cx from 'classnames';
import URI from 'urijs';

// ducks
import { paginateThis, sortFields, PROPERTY_TYPE_MAP } from '../../ducks/PropertySearch';

// middleware
import { cleanQuery } from '../../middleware/client';

// containers
import PropertySearchForm from './PropertySearchForm';
import PropertySearchAdvanced from './PropertySearchAdvanced';
import PropertySearchResults from './PropertySearchResults';
import PropertySearchFilterModal from './PropertySearchFilterModal';

// components
import Paginator from '../../components/paginator/paginator';

// modules
import BS from '../../theme/Bootstrap';
import styles from '../Containers.module.scss';

const BREADCRUMB_TITLES = {
  ...PROPERTY_TYPE_MAP,
  residential: 'Residential For Sale',
  commercial: 'Commercial Properties'
};

class PropertySearch extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  render() {
    const {
      query,
      location: { pathname },
      hideBreadcrumb
    } = this.props;
    const { handleChange, handleSubmit } = this;
    const initialValues = omit(
      query,
      'page',
      'pageSize',
      'sort',
      'sortOrder',
      'pointOfInterestId',
      'communityName',
      'golfCourseId',
      'communityId',
      'firmId'
    );
    const linkQuery = cleanQuery(query);
    const { propertyClass = 'residential' } = initialValues;
    const searchTo = URI(this.props.pathname)
      .search({
        propertyClass: propertyClass !== 'residential' ? propertyClass : undefined
      })
      .toString();

    const sortFieldsFiltered = filter(
      sortFields,
      field => field.propertyClass.indexOf(propertyClass) !== -1
    );

    return (
      <Container fluid className={styles.PropertySearch}>
        <Switch>
          <Route
            path={this.props.pathnameAdvanced}
            exact
            render={() => (
              <React.Fragment>
                {!hideBreadcrumb && (
                  <Container>
                    <Row>
                      <Breadcrumb>
                        <LinkContainer exact to="/">
                          <BreadcrumbItem>
                            <Link to="/">Home</Link>
                          </BreadcrumbItem>
                        </LinkContainer>
                        <LinkContainer exact to={searchTo}>
                          <BreadcrumbItem>
                            <Link to={searchTo}>Property Search</Link>
                          </BreadcrumbItem>
                        </LinkContainer>
                        <BreadcrumbItem active>
                          Advanced {PROPERTY_TYPE_MAP[propertyClass]} Search
                        </BreadcrumbItem>
                      </Breadcrumb>
                    </Row>
                  </Container>
                )}
                <PropertySearchAdvanced
                  enableReinitialize={true}
                  initialValues={{ ...initialValues, countOnly: 1 }}
                  propertyClass={propertyClass}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                  pathname={this.props.pathnameAdvanced}
                  pathnameResults={this.props.pathnameAdvancedResults}
                />
              </React.Fragment>
            )}
          />
          <Route
            path={this.props.pathname}
            render={() => (
              <React.Fragment>
                {!hideBreadcrumb && (
                  <Container>
                    <Row>
                      <Breadcrumb>
                        <LinkContainer exact to="/">
                          <BreadcrumbItem>
                            <Link to="/">Home</Link>
                          </BreadcrumbItem>
                        </LinkContainer>
                        <LinkContainer exact to={searchTo}>
                          <BreadcrumbItem>
                            <Link to={searchTo}>Property Search</Link>
                          </BreadcrumbItem>
                        </LinkContainer>
                        <BreadcrumbItem active>
                          {BREADCRUMB_TITLES[propertyClass]}
                        </BreadcrumbItem>
                      </Breadcrumb>
                    </Row>
                  </Container>
                )}
                <PropertySearchForm
                  enableReinitialize={true}
                  initialValues={initialValues}
                  basePath={this.props.pathname}
                  onFilterClick={() => {
                    this.setState({
                      showModal: true
                    });
                  }}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                />
              </React.Fragment>
            )}
          />
        </Switch>
        <Route path={this.props.pathnameAdvanced}>
          {props => {
            return !props.match || pathname === this.props.pathnameAdvancedResults ? (
              <React.Fragment>
                <Row className={cx(styles.searchPagination, BS.pt3)}>
                  <Container>
                    <Paginator
                      listId="properties"
                      to={pathname}
                      query={linkQuery}
                      sortFields={sortFieldsFiltered}
                      top
                    >
                      <ButtonGroup className={`${BS.inputGroupGutter}`}>
                        <LinkContainer
                          exact
                          to={URI(pathname)
                            .search({
                              ...linkQuery,
                              view: undefined,
                              page: linkQuery.view !== 'map' ? linkQuery.page : 1,
                              pageSize: linkQuery.view !== 'map' ? linkQuery.pageSize : 15
                            })
                            .toString()}
                        >
                          <Button
                            color={cx({
                              secondary: linkQuery.view === 'thumb',
                              'dark-blue': linkQuery.view !== 'thumb'
                            })}
                            className={cx(BS.mb3)}
                          >
                            Thumb
                          </Button>
                        </LinkContainer>
                        <LinkContainer
                          exact
                          to={URI(pathname)
                            .search({
                              ...linkQuery,
                              view: 'map',
                              page: linkQuery.view === 'map' ? linkQuery.page : 1,
                              pageSize:
                                linkQuery.view === 'map' ? linkQuery.pageSize : 100
                            })
                            .toString()}
                        >
                          <Button
                            color={cx({
                              secondary: linkQuery.view === 'map',
                              'dark-blue': linkQuery.view !== 'map'
                            })}
                            className={cx(BS.mb3)}
                          >
                            Map
                          </Button>
                        </LinkContainer>
                      </ButtonGroup>
                    </Paginator>
                  </Container>
                </Row>
                <PropertySearchResults
                  listId="properties"
                  propertyClass={propertyClass}
                />
                <Row className={cx(styles.searchPagination, BS.mt3)}>
                  <Container>
                    <Paginator
                      listId="properties"
                      to={pathname}
                      query={linkQuery}
                      sortFields={sortFieldsFiltered}
                      bottom
                    />
                  </Container>
                </Row>
              </React.Fragment>
            ) : null;
          }}
        </Route>
        {this.state.showModal && (
          <PropertySearchFilterModal
            isOpen={this.state.showModal}
            onToggle={showModal => {
              this.setState({ showModal });
            }}
            enableReinitialize={true}
            initialValues={initialValues}
            onChange={handleChange}
            onSubmit={handleSubmit}
          />
        )}
      </Container>
    );
  }

  handleChange(values, dispatch, props, previousValues) {
    clearTimeout(this.onChangeSubmitTimeout);
    if (!props.pristine) {
      this.onChangeSubmitTimeout = setTimeout(() => {
        props.submit();
      }, 100);
    }
  }

  handleSubmit(values, dispatch, props) {
    const {
      submitSearch,
      location: { pathname }
    } = this.props;

    return submitSearch({
      to: pathname,
      values: values
    });
  }

  componentWillUnmount() {
    this.props.clearResults();
  }

  componentDidUpdate(prevProps) {
    const prevPathname = prevProps.location.pathname,
      pathname = this.props.location.pathname,
      propertyClass = this.props.query.propertyClass,
      oldPropertyClass = prevProps.query.propertyClass;

    if (this.props.query.page !== prevProps.query.page) {
      window.scrollTo(0, 0);
    }

    if (oldPropertyClass !== propertyClass) {
      this.props.resetResults([]);
    }

    if (
      prevPathname === this.props.pathnameAdvanced &&
      (pathname === this.props.pathnameAdvancedResults ||
        pathname === this.props.pathname)
    ) {
      this.props.expire();
    }
  }
}

const PropertySearchDecorated = paginateThis.decorate(PropertySearch, (state, props) => {
  return {
    propertyClass: props.propertyClass,
    address: '',
    minBed: '',
    maxBed: '',
    minPrice: '',
    maxPrice: '',
    numBaths: '',
    propertyType: '',
    buildingType: '',
    mlsNumber: '',
    // advanced
    minArea: '',
    maxArea: '',
    shortSale: '',
    minYear: '',
    maxYear: '',
    foreclosed: '',
    ohStartDate: '',
    ohEndDate: '',
    waterfront: '',
    communityType: '',
    pets: '',
    city: '',
    amenities: '',
    privatePool: '',
    minGarageSpaces: '',
    maxGarageSpaces: '',
    furnished: '',
    status: '',
    // resdiential_income
    minGrossIncome: '',
    maxGrossIncome: '',
    // quick searches
    waterfrontYN: '',
    // commercial
    transactionType: '',
    commercialType: '',
    minLandArea: '',
    maxLandArea: '',
    // rental
    availabilityType: '',
    rnTerm: '',
    // custom
    pointOfInterestId: props.pointOfInterestId || '',
    communityId: props.communityId || '',
    communityName: props.communityName || '',
    golfCourseId: props.golfCourseId || '',
    agentId: props.agentId || '',
    recentlySold: props.recentlySold || '',
    firm: props.firm || '',
    firmId: props.firmId || ''
  };
});

PropertySearchDecorated.defaultProps = {
  pathname: '/find-naples-home',
  pathnameAdvanced: '/find-naples-home/advanced',
  pathnameAdvancedResults: '/find-naples-home/advanced/results',
  propertyClass: 'residential'
};

export default PropertySearchDecorated;



// WEBPACK FOOTER //
// ./src/containers/PropertySearch/PropertySearch.js